// src/encounter/services/use-org-specialities.ts
// [PROD-3336] The clinic's own specialities, for the referral target (`referrals` switch): active and not deleted, allied
// specialities included, each with whether an active practitioner holds it. One read per org, cached; typing filters it in
// memory (lib/referral-specialities.ts), so a keystroke sends nothing.
//
// Contract (PROD-3336, Practice.Aggregator, org from the token): GET /api/v1/Practice/Specialities →
// [{ specialityId: guid, name, hasActivePractitioner }], sorted by name: the specialities of the org's non-deleted
// practitioners (main + extra, active links only), names from Master. A failed read resolves to NO rows, and the pickers
// then offer the Dietitian row (FALLBACK_SPECIALITIES), so the referral never loses Dietitian.
import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import { useAuthStore } from '@/auth/auth-store';
import type { ReferralSpeciality } from '../lib/referral-specialities';

export const ORG_SPECIALITIES_PATH = '/api/v1/Practice/Specialities';

/** The wire row. */
interface OrgSpecialityRow {
  specialityId?: string | null;
  name?: string | null;
  hasActivePractitioner?: boolean | null;
}

function toOrgSpeciality(row: OrgSpecialityRow): ReferralSpeciality | null {
  const id = row.specialityId?.trim();
  const name = row.name?.trim();
  if (!id || !name) return null;
  return { id, name, hasActivePractitioner: row.hasActivePractitioner ?? undefined };
}

export const orgSpecialitiesQueryKey = (organizationId: string | null | undefined) => ['org-specialities', organizationId ?? null];

/** The org's specialities, sorted by name. `enabled` false (the switch off, or not asked yet) sends nothing. */
export function useOrgSpecialities(enabled: boolean) {
  const organizationId = useAuthStore((s) => s.user?.organizationId ?? null);
  return useQuery<ReferralSpeciality[]>({
    queryKey: orgSpecialitiesQueryKey(organizationId),
    enabled: enabled && !!organizationId,
    retry: false,
    staleTime: 5 * 60_000,
    queryFn: async ({ signal }) => {
      try {
        const rows = await get<OrgSpecialityRow[]>(ORG_SPECIALITIES_PATH, { signal });
        return (Array.isArray(rows) ? rows : [])
          .map(toOrgSpeciality)
          .filter((s): s is ReferralSpeciality => s !== null)
          .sort((a, b) => a.name.localeCompare(b.name));
      } catch {
        // No error card: the pickers fall back to the Dietitian row. Nothing is logged.
        return [];
      }
    },
  });
}
